import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Skeleton,
  Alert,
  Paper,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Photo as PhotoIcon,
  Visibility as ViewIcon,
  MoreVert as MoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchManuals, uploadPDFManual, createPhotoManual, deleteManual } from '../../store/slices/manualsSlice';
import { Machine } from '../../types/machine';
import { Manual } from '../../types/manual';
import { format, isValid, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

interface MachineManualsTabProps {
  machine: Machine;
}

// Helper function to safely format dates
const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString || dateString === 'null' || dateString === 'undefined') {
    return 'Nicht verfügbar';
  }
  
  try {
    let date: Date;
    
    if (typeof dateString === 'string') {
      if (dateString.trim() === '' || dateString === '0000-00-00T00:00:00Z') {
        return 'Nicht verfügbar';
      }
      date = parseISO(dateString);
    } else {
      date = new Date(dateString as any);
    }
    
    if (!isValid(date) || date.getFullYear() < 1900) {
      return 'Ungültiges Datum';
    }
    
    return format(date, 'dd.MM.yyyy', { locale: de });
  } catch (error) {
    console.warn('Error formatting date in MachineManualsTab:', dateString, error);
    return 'Ungültiges Datum';
  }
};

export const MachineManualsTab: React.FC<MachineManualsTabProps> = ({ machine }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { manuals, isLoading, isUploading } = useAppSelector((state) => state.manuals);
  const { user } = useAppSelector((state) => state.auth);
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedManual, setSelectedManual] = useState<Manual | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploadType, setUploadType] = useState<'pdf' | 'photo'>('pdf');
  const [manualTitle, setManualTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  
  // Debug state
  const [debugData, setDebugData] = useState<any>(null);
  const [showDebugDialog, setShowDebugDialog] = useState(false);

  useEffect(() => {
    dispatch(fetchManuals(machine.id));
  }, [dispatch, machine.id]);

  const canEdit = user?.role === 'admin';
  const machineManuals = manuals.filter(manual =>
    manual && manual.machines?.some(m => m.id === machine.id)
  );

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, manual: Manual) => {
    setAnchorEl(event.currentTarget);
    setSelectedManual(manual);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedManual(null);
  };

  const handleViewManual = (manual: Manual) => {
    navigate(`/manuals/${manual.id}`);
    handleMenuClose();
  };

  const handleDeleteManual = () => {
    setDeleteDialogOpen(true);
    setAnchorEl(null); // Close menu but keep selectedManual
  };

  const handleConfirmDelete = async () => {
    if (selectedManual) {
      await dispatch(deleteManual(selectedManual.id));
    }
    setDeleteDialogOpen(false);
    setSelectedManual(null); // Clear selection after delete
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedManual(null); // Clear selection when cancelled
  };

  const handleOpenUploadDialog = (type: 'pdf' | 'photo') => {
    setUploadType(type);
    setUploadDialogOpen(true);
    setManualTitle('');
    setSelectedFile(null);
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setManualTitle('');
    setSelectedFile(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[DEBUG] File selection started', {
      hasFiles: !!event.target.files,
      filesLength: event.target.files?.length,
      inputValue: event.target.value
    });

    const file = event.target.files?.[0];
    
    if (file) {
      console.log('[DEBUG] File selected', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        isPDF: file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
      });

      // Validate file type
      const isPDF = file.type === 'application/pdf' || 
                   file.type === 'application/x-pdf' || 
                   file.name.toLowerCase().endsWith('.pdf');
      
      if (!isPDF) {
        console.error('[ERROR] Invalid file type selected:', file.type);
        alert('Bitte wählen Sie eine PDF-Datei aus.');
        return;
      }

      // Validate file size (50MB limit from env)
      const maxSize = 52428800; // 50MB
      if (file.size > maxSize) {
        console.error('[ERROR] File too large:', file.size);
        alert(`Datei ist zu groß. Maximum: ${maxSize / 1024 / 1024}MB`);
        return;
      }

      setSelectedFile(file);
      
      // Auto-generate title from filename
      if (!manualTitle) {
        const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
        setManualTitle(nameWithoutExtension);
      }

      console.log('[DEBUG] File successfully set to state');
    } else {
      console.warn('[WARN] No file selected or file is null');
      setSelectedFile(null);
    }
  };

  const collectDebugData = (file: File | null, result: any, error: any = null) => {
    const timestamp = new Date().toISOString();
    const debugInfo = {
      timestamp,
      environment: {
        userAgent: navigator.userAgent,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        windowSize: `${window.innerWidth}x${window.innerHeight}`,
        isMobile: window.innerWidth < 768,
        cookiesEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
      },
      uploadDetails: {
        uploadType,
        manualTitle,
        machineId: machine.id,
        machineName: machine.name,
      },
      fileInfo: file ? {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: new Date(file.lastModified).toISOString(),
        sizeInMB: (file.size / 1024 / 1024).toFixed(2),
        isValidPDF: file.type === 'application/pdf' || file.type === 'application/x-pdf' || file.name.toLowerCase().endsWith('.pdf'),
        constructor: file.constructor.name,
        stream: !!file.stream,
        slice: !!file.slice,
        filePresent: true,
      } : {
        filePresent: false,
        selectedFileState: !!selectedFile,
        selectedFileName: selectedFile?.name || 'null',
        name: null,
        size: 0,
        type: null,
        lastModified: null,
        sizeInMB: '0',
        isValidPDF: false,
        constructor: null,
        stream: false,
        slice: false,
      },
      fileValidation: {
        hasSelectedFile: !!selectedFile,
        fileMatchesParameter: selectedFile === file,
        uploadTypeIsPDF: uploadType === 'pdf',
        titlePresent: !!manualTitle,
        canProceed: !!(manualTitle && selectedFile && uploadType === 'pdf'),
      },
      apiConfig: {
        baseURL: import.meta.env.VITE_API_URL || '/api/v1',
        endpoint: `/machines/${machine.id}/manuals/pdf`,
        fullURL: `${import.meta.env.VITE_API_URL || '/api/v1'}/machines/${machine.id}/manuals/pdf`,
      },
      authentication: {
        hasToken: !!localStorage.getItem('token'),
        tokenPrefix: localStorage.getItem('token')?.substring(0, 20) + '...',
      },
      result: {
        type: result?.type,
        meta: result?.meta ? {
          ...result.meta,
          arg: result.meta.arg ? {
            machineId: result.meta.arg.machineId,
            title: result.meta.arg.title,
            filePresent: !!result.meta.arg.file,
            fileName: result.meta.arg.file?.name,
            fileType: result.meta.arg.file?.type,
            fileSize: result.meta.arg.file?.size,
          } : null,
        } : null,
        payload: result?.payload,
        error: result?.error,
        fulfilled: uploadPDFManual.fulfilled.match(result),
        rejected: uploadPDFManual.rejected.match(result),
      },
      exception: error ? {
        message: error.message,
        stack: error.stack,
        name: error.name,
      } : null,
    };
    
    return debugInfo;
  };

  const handleUpload = async () => {
    if (!manualTitle) {
      console.error('[ERROR] No manual title provided');
      return;
    }

    console.log('[DEBUG] Upload attempt started', {
      uploadType,
      hasSelectedFile: !!selectedFile,
      selectedFileName: selectedFile?.name,
      manualTitle
    });

    try {
      if (uploadType === 'pdf' && selectedFile) {
        // Validate file exists and is still valid before upload
        console.log('[DEBUG] Pre-upload file validation', {
          fileExists: !!selectedFile,
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
          fileType: selectedFile.type,
          fileConstructor: selectedFile.constructor.name
        });

        // Additional validation that file is still accessible
        try {
          const canReadFile = selectedFile.size > 0 && selectedFile.name && selectedFile.type;
          console.log('[DEBUG] File accessibility check:', canReadFile);
          
          if (!canReadFile) {
            throw new Error('File appears to be invalid or inaccessible');
          }
        } catch (fileError) {
          console.error('[ERROR] File validation failed:', fileError);
          // Still attempt upload but log the issue
        }

        // Pass File object directly to Redux action (avoiding serialization issues)
        console.log('[DEBUG] Dispatching Redux action with File object');

        const result = await dispatch(uploadPDFManual({
          machineId: machine.id,
          title: manualTitle,
          file: selectedFile,
        }));
        
        console.log('[DEBUG] Upload dispatch completed', {
          resultType: result.type,
          hasPayload: !!result.payload
        });
        
        // Collect debug data
        const debugInfo = collectDebugData(selectedFile, result);
        setDebugData(debugInfo);
        
        // Always show debug dialog after upload attempt
        setShowDebugDialog(true);
        
        // Check if the upload was successful
        if (uploadPDFManual.fulfilled.match(result)) {
          // Refresh the manuals list to show the new upload
          dispatch(fetchManuals(machine.id));
          handleCloseUploadDialog();
        }
      } else if (uploadType === 'pdf' && !selectedFile) {
        console.error('[ERROR] PDF upload attempted but no file selected');
        const debugInfo = collectDebugData(null, null, new Error('No file selected for PDF upload'));
        setDebugData(debugInfo);
        setShowDebugDialog(true);
      } else if (uploadType === 'photo') {
        console.log('[DEBUG] Creating photo manual with title:', manualTitle);
        
        try {
          const result = await dispatch(createPhotoManual({
            machineId: machine.id,
            title: manualTitle,
          }));
          
          // Create debug message for mobile
          const debugInfo = {
            type: result.type,
            haypayload: !!result.payload,
            payloadType: typeof result.payload,
            fulfilled: createPhotoManual.fulfilled.match(result),
            rejected: createPhotoManual.rejected.match(result)
          };
          
          console.log('[DEBUG] Photo manual creation result:', debugInfo);
          
          if (createPhotoManual.fulfilled.match(result)) {
            // Show debug info in alert for mobile debugging
            const payloadStr = JSON.stringify(result.payload, null, 2);
            if (window.confirm(`Debug: Manual created successfully.\n\nPayload: ${payloadStr.substring(0, 200)}...\n\nClick OK to continue to upload page.`)) {
              // Try different paths to get the ID
              const manualId = result.payload?.id || 
                        result.payload?.manual?.id || 
                        result.payload?._id ||
                        result.payload;
              
              if (manualId && manualId !== '[object Object]') {
                console.log('[DEBUG] Navigating to:', `/manuals/${manualId}/upload`);
                
                // Close dialog first
                handleCloseUploadDialog();
                
                // Navigate immediately
                navigate(`/manuals/${manualId}/upload`);
                
                // Also refresh the manuals list
                dispatch(fetchManuals(machine.id));
              } else {
                alert(`Manual wurde erstellt, aber ID konnte nicht extrahiert werden.\n\nPayload: ${payloadStr}`);
                // Still refresh to show the new manual
                dispatch(fetchManuals(machine.id));
                handleCloseUploadDialog();
              }
            }
          } else if (createPhotoManual.rejected.match(result)) {
            console.error('[ERROR] Photo manual creation was rejected:', result);
            alert(`Fehler beim Erstellen der Foto-Anleitung: ${result.payload || 'Unbekannter Fehler'}`);
          } else {
            console.error('[ERROR] Unexpected result type:', result);
            alert('Ein unerwarteter Fehler ist aufgetreten.');
          }
        } catch (error) {
          console.error('[ERROR] Exception in photo manual creation:', error);
          alert(`Ein Fehler ist aufgetreten: ${error}`);
        }
      }
    } catch (error) {
      // Collect debug data for exceptions
      const debugInfo = collectDebugData(selectedFile, null, error);
      setDebugData(debugInfo);
      setShowDebugDialog(true);
    }
  };



  const renderManualCard = (manual: Manual) => (
    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={manual.id}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: (theme) => theme.shadows[4],
          },
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Avatar
              sx={{
                bgcolor: manual.type === 'pdf' ? 'error.main' : 'primary.main',
                width: 48,
                height: 48,
              }}
            >
              {manual.type === 'pdf' ? <PdfIcon /> : <PhotoIcon />}
            </Avatar>
            {canEdit && (
              <IconButton
                size="small"
                onClick={(e) => handleMenuClick(e, manual)}
              >
                <MoreIcon />
              </IconButton>
            )}
          </Box>
          
          <Typography variant="h6" component="h3" gutterBottom noWrap>
            {manual.title}
          </Typography>
          
          <Box display="flex" gap={1} mb={2}>
            <Chip
              label={manual.type.toUpperCase()}
              size="small"
              color={manual.type === 'pdf' ? 'error' : 'primary'}
            />
            <Chip
              label={`${manual.page_count || (manual as any).manual_pdfs?.[0]?.page_count || 'N/A'} Seiten`}
              size="small"
              variant="outlined"
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            Erstellt: {formatDate(manual.created_at)}
          </Typography>
        </CardContent>
        
        <CardActions>
          <Button
            size="small"
            startIcon={<ViewIcon />}
            onClick={() => handleViewManual(manual)}
          >
            Anzeigen
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );

  const renderSkeletonCards = () =>
    Array.from({ length: 3 }).map((_, index) => (
      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`skeleton-${index}`}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Skeleton variant="circular" width={48} height={48} />
              <Skeleton variant="circular" width={24} height={24} />
            </Box>
            <Skeleton variant="text" height={32} width="80%" />
            <Box display="flex" gap={1} mb={2}>
              <Skeleton variant="rectangular" width={60} height={24} />
              <Skeleton variant="rectangular" width={80} height={24} />
            </Box>
            <Skeleton variant="text" height={20} width="60%" />
          </CardContent>
          <CardActions>
            <Skeleton variant="rectangular" width={100} height={32} />
          </CardActions>
        </Card>
      </Grid>
    ));

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Bedienungsanleitungen ({machineManuals.length})
        </Typography>
        {canEdit && (
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<PhotoIcon />}
              onClick={() => handleOpenUploadDialog('photo')}
            >
              Foto-Anleitung
            </Button>
            <Button
              variant="contained"
              startIcon={<PdfIcon />}
              onClick={() => handleOpenUploadDialog('pdf')}
            >
              PDF hochladen
            </Button>
            {debugData && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowDebugDialog(true)}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                Debug
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Manuals Grid */}
      <Grid container spacing={3}>
        {isLoading ? renderSkeletonCards() : machineManuals.map(renderManualCard)}
      </Grid>

      {/* Empty State */}
      {!isLoading && machineManuals.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={8}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Keine Anleitungen vorhanden
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fügen Sie PDF-Dokumente oder Foto-Anleitungen hinzu.
          </Typography>
          {canEdit && (
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<PhotoIcon />}
                onClick={() => handleOpenUploadDialog('photo')}
              >
                Foto-Anleitung erstellen
              </Button>
              <Button
                variant="contained"
                startIcon={<PdfIcon />}
                onClick={() => handleOpenUploadDialog('pdf')}
              >
                PDF hochladen
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewManual(selectedManual!)}>
          <ViewIcon sx={{ mr: 1 }} />
          Anzeigen
        </MenuItem>
        <MenuItem onClick={handleDeleteManual} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Löschen
        </MenuItem>
      </Menu>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleCloseUploadDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {uploadType === 'pdf' ? 'PDF-Anleitung hochladen' : 'Foto-Anleitung erstellen'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Titel"
            fullWidth
            variant="outlined"
            value={manualTitle}
            onChange={(e) => setManualTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          {uploadType === 'pdf' && (
            <Box>
              <input
                accept=".pdf,application/pdf,application/x-pdf"
                style={{ display: 'none' }}
                id="pdf-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="pdf-upload">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  PDF-Datei auswählen
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="body2" color="text.secondary">
                  Ausgewählt: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </Typography>
              )}
            </Box>
          )}
          
          {uploadType === 'photo' && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Nach dem Erstellen werden Sie zur Kamera weitergeleitet, um die einzelnen Seiten der Anleitung zu fotografieren.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sie können beliebig viele Seiten nacheinander aufnehmen und bei Bedarf einzelne Seiten wiederholen.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog}>
            Abbrechen
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!manualTitle || (uploadType === 'pdf' && !selectedFile) || isUploading}
          >
            {uploadType === 'pdf' ? 'Hochladen' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manual löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Sind Sie sicher, dass Sie das Manual "{selectedManual?.title}" löschen möchten? 
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>



      {/* Debug Information Dialog */}
      <Dialog
        open={showDebugDialog}
        onClose={() => setShowDebugDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 768}
      >
        <DialogTitle>
          Upload Debug Information
          <IconButton
            onClick={() => setShowDebugDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {debugData && (
            <Box sx={{ p: 2 }}>
              {/* Status Summary */}
              <Alert 
                severity={debugData.result?.fulfilled ? 'success' : 'error'}
                sx={{ mb: 2 }}
              >
                {debugData.result?.fulfilled ? 'Upload Successful' : 'Upload Failed'}
                {debugData.result?.payload && ` - ${debugData.result.payload}`}
              </Alert>

              {/* Environment Info */}
              <Typography variant="h6" gutterBottom>Environment</Typography>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Grid container spacing={1}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2"><strong>Screen:</strong> {debugData.environment.screenSize}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2"><strong>Window:</strong> {debugData.environment.windowSize}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2"><strong>Mobile:</strong> {debugData.environment.isMobile ? 'Yes' : 'No'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2"><strong>Online:</strong> {debugData.environment.onLine ? 'Yes' : 'No'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2"><strong>User Agent:</strong></Typography>
                    <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                      {debugData.environment.userAgent}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* File Info */}
              {debugData.fileInfo && (
                <>
                  <Typography variant="h6" gutterBottom>File Information</Typography>
                  <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="body2"><strong>Name:</strong> {debugData.fileInfo.name}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2"><strong>Size:</strong> {debugData.fileInfo.sizeInMB} MB</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2"><strong>Type:</strong> {debugData.fileInfo.type}</Typography>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="body2"><strong>Modified:</strong> {debugData.fileInfo.lastModified}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </>
              )}

              {/* API Config */}
              <Typography variant="h6" gutterBottom>API Configuration</Typography>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2"><strong>Base URL:</strong> {debugData.apiConfig.baseURL}</Typography>
                <Typography variant="body2"><strong>Endpoint:</strong> {debugData.apiConfig.endpoint}</Typography>
                <Typography variant="body2"><strong>Full URL:</strong> {debugData.apiConfig.fullURL}</Typography>
                <Typography variant="body2"><strong>Has Token:</strong> {debugData.authentication.hasToken ? 'Yes' : 'No'}</Typography>
                {debugData.authentication.hasToken && (
                  <Typography variant="body2"><strong>Token:</strong> {debugData.authentication.tokenPrefix}</Typography>
                )}
              </Paper>

              {/* Upload Result */}
              <Typography variant="h6" gutterBottom>Upload Result</Typography>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2"><strong>Action Type:</strong> {debugData.result?.type || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Fulfilled:</strong> {debugData.result?.fulfilled ? 'Yes' : 'No'}</Typography>
                <Typography variant="body2"><strong>Rejected:</strong> {debugData.result?.rejected ? 'Yes' : 'No'}</Typography>
                {debugData.result?.payload && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2"><strong>Response:</strong></Typography>
                    <Typography 
                      component="pre" 
                      variant="caption" 
                      sx={{ 
                        bgcolor: 'grey.100', 
                        p: 1, 
                        borderRadius: 1, 
                        fontSize: '0.7rem',
                        overflow: 'auto',
                        maxHeight: 200,
                        wordBreak: 'break-all'
                      }}
                    >
                      {JSON.stringify(debugData.result.payload, null, 2)}
                    </Typography>
                  </Box>
                )}
              </Paper>

              {/* Exception Info */}
              {debugData.exception && (
                <>
                  <Typography variant="h6" gutterBottom>Exception Details</Typography>
                  <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
                    <Typography variant="body2"><strong>Message:</strong> {debugData.exception.message}</Typography>
                    <Typography variant="body2"><strong>Type:</strong> {debugData.exception.name}</Typography>
                    {debugData.exception.stack && (
                      <Typography 
                        component="pre" 
                        variant="caption" 
                        sx={{ 
                          bgcolor: 'rgba(0,0,0,0.1)', 
                          p: 1, 
                          borderRadius: 1, 
                          fontSize: '0.7rem',
                          overflow: 'auto',
                          maxHeight: 150,
                          mt: 1
                        }}
                      >
                        {debugData.exception.stack}
                      </Typography>
                    )}
                  </Paper>
                </>
              )}

              {/* Raw Debug Data */}
              <Typography variant="h6" gutterBottom>Raw Debug Data</Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'white' }}>
                <Typography 
                  component="pre" 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.7rem',
                    overflow: 'auto',
                    maxHeight: 300,
                    wordBreak: 'break-all'
                  }}
                >
                  {JSON.stringify(debugData, null, 2)}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => navigator.clipboard?.writeText(JSON.stringify(debugData, null, 2))}
            variant="outlined"
          >
            Copy Debug Data
          </Button>
          <Button onClick={() => setShowDebugDialog(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};